<template>
    <Head :title="title">
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
        <link
            rel="stylesheet"
            href="https://fonts.googleapis.com/css2?family=Geist:wght@500;600;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@500&display=swap"
        />
    </Head>

    <PlayerShell
        ref="shellRef"
        :tracks="pair.tracks"
        :same-timeline="pair.sameTimeline"
        :locale="locale"
        :theme="rootStyle"
        :traffic-blocked="trafficBlocked"
        :unavailable="!video?.available"
        :label="showLabel"
        :branding="!!podcast.branding"
        :media-session="{ artist: podcast.title, album: podcast.title }"
        @player-event="analytics.handle"
    >
        <template #unavailable>
            <VideoUnavailable :title="t('Video not available')" :body="unavailableBody" :action-url="audioFallbackUrl" :action-label="t('Listen to the audio')" />
        </template>

        <template #top-bar-actions>
            <button v-if="podcast.share" type="button" class="vp-icon-button" :aria-label="t('Share')" @click="onShare">
                <VideoIcon name="share" />
            </button>
            <span v-if="shareFeedback" class="vp-top-feedback" role="status">{{ shareFeedback }}</span>
        </template>

        <template #poster="{ play, chapterCount, hasTranscript }">
            <VideoPoster
                slot="centered-chrome"
                :podcast="podcast"
                :episode="episode"
                :image="posterImage"
                :duration="video.duration"
                :chapter-count="chapterCount"
                :has-transcript="hasTranscript"
                @play="play"
            />
        </template>

        <template #end-screen="{ replay }">
            <VideoEndScreen slot="centered-chrome" :episode="episode" :podcast="podcast" :next-episode="nextEpisode" :all-episodes-url="allEpisodesUrl" @replay="replay" />
        </template>
    </PlayerShell>
</template>

<script setup>
import { computed, onMounted, ref, shallowRef } from "vue"
import { Head } from "@inertiajs/vue3"
import PlayerShell from "../PlayerCore/PlayerShell.vue"
import VideoIcon from "../PlayerCore/Components/VideoIcon.vue"
import VideoUnavailable from "../PlayerCore/Components/VideoUnavailable.vue"
import { useAudioWorker } from "../Player/useAudioWorker"
import VideoEndScreen from "../VideoPlayer/Components/VideoEndScreen.vue"
import VideoPoster from "../VideoPlayer/Components/VideoPoster.vue"
import { createPodcastAnalytics } from "../VideoPlayer/podcastAnalytics.js"
import { buildPodcastTracks } from "../VideoPlayer/podcastTracks.js"
import { themeVars } from "../VideoPlayer/theme.js"
import { useVideoAnalytics } from "../VideoPlayer/useVideoAnalytics.js"
import { provideVideoI18n } from "../VideoPlayer/useVideoI18n.js"
import { createViewSessionId } from "../VideoPlayer/viewSession.js"

const props = defineProps({
    podcast: { type: Object, required: true },
    episode: { type: Object, required: true },
    video: { type: Object, default: null },
    videoSignature: { type: String, default: null },
    videoExpires: { type: Number, default: null },
    audioFallbackUrl: { type: String, required: true },
    allEpisodesUrl: { type: String, required: true },
    nextEpisode: { type: Object, default: null },
    colors: { type: Object, default: null },
    theme: { type: String, default: "default" },
    preview: { type: Boolean, default: false },
    // Pro's preview signature (preview/signature/expires); null outside a signed preview.
    previewQuery: { type: Object, default: null },
    // The website's member signature (mexp/msig) for a members-only show; null otherwise.
    memberQuery: { type: Object, default: null },
    trafficBlocked: { type: Boolean, default: false },
})

const { t, locale } = provideVideoI18n(props.podcast.locale)
const unavailableBody = t("This episode doesn't have a video stream. Listen to the audio version instead.")
const rootStyle = computed(() => themeVars(props.colors, props.theme))
const shellRef = ref(null)
const shareFeedback = ref("")

const showLabel = computed(() =>
    [props.podcast.title, props.episode.episode > 0 ? t("Ep. {n}", { n: props.episode.episode }) : null].filter(Boolean).join(" · "),
)
const title = computed(() => `${props.episode?.title ?? ""} — ${props.podcast.title}`)
const posterImage = computed(() => props.video?.poster_url || props.episode.artwork || props.podcast.artwork || null)

// One view session per page load, shared by the HLS URLs and the beacons.
const viewSession = createViewSessionId()

// VideoStreamController's routes require signature/expires (SC-1256); PodcastController::
// videoPlayer() mints them server-side for this page. `vs` and `preview` ride along unsigned: the
// server carries both onto every variant and segment URL, and the segment redirect turns them
// into `vid_session` / `vid_internal` for the CDN statistics.
const masterUrl = props.video?.available
    ? route("podcast.video.master", {
          podcast: props.podcast.hash_id,
          episode: props.episode.hash_id,
          version: props.video.version,
          signature: props.videoSignature,
          expires: props.videoExpires,
          vs: viewSession,
          ...(props.preview ? { preview: "true" } : {}),
      })
    : null

const pair = buildPodcastTracks({
    podcast: props.podcast,
    episode: props.episode,
    video: props.video,
    masterUrl,
    transcriptUrl: (lang) =>
        route("podcast.transcription", {
            podcast: props.podcast.hash_id,
            episode: props.episode.hash_id,
            lang,
            ...(props.previewQuery ?? {}),
            ...(props.memberQuery ?? {}),
        }),
})

const videoAnalytics = props.video?.available
    ? useVideoAnalytics({
          podcastHash: props.podcast.hash_id,
          episodeHash: props.episode.hash_id,
          duration: props.video.duration,
          logUrl: route("podcast.listen-video", { podcast: props.podcast.hash_id, ...(props.memberQuery ?? {}) }),
          preview: props.preview,
          viewSession,
      })
    : null

// The audio side of the switch logs like the audio player (POST podcast.listen). useAudioWorker
// reads the <audio> element's duration, which the shell owns: it is handed over on mount.
const audioTimeline = shallowRef(null)
const audioWorker = pair.sameTimeline
    ? useAudioWorker(audioTimeline, route("podcast.listen", { podcast: props.podcast.hash_id, ...(props.previewQuery ?? {}), ...(props.memberQuery ?? {}) }), props.preview)
    : null

if (audioWorker) {
    audioWorker.podcastId.value = props.podcast.hash_id
    audioWorker.episodeId.value = props.episode.hash_id
    audioWorker.episodeDuration.value = Math.ceil(props.episode.audio?.duration ?? 0)
}

const analytics = createPodcastAnalytics({ video: videoAnalytics, audio: audioWorker })

onMounted(() => {
    audioTimeline.value = shellRef.value?.audioElement() ?? null
})

async function onShare() {
    // Keep ?theme= like allEpisodesUrl and nextEpisode.url do.
    const theme = new URLSearchParams(window.location.search).get("theme")
    const url = route("podcast.video", { podcast: props.podcast.hash_id, episode: props.episode.hash_id, ...(theme ? { theme } : {}) })

    try {
        if (navigator.share) {
            await navigator.share({ title: title.value, url })

            return
        }

        await navigator.clipboard.writeText(url)
        shareFeedback.value = t("Link copied")
        setTimeout(() => {
            shareFeedback.value = ""
        }, 2000)
    } catch (error) {
        // AbortError when the viewer closes the share sheet; nothing to report.
    }
}
</script>
